import React from 'react';
import CustomIcon from '../Common/CustomIcon/CustomIcon.js';
import kali from 'kali';
import md5 from 'md5';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import {
	selectComponents,
} from './utils/reactSelectUtils';
import TemplateSkeleton from './TemplateSkeleton.js';
import {
	v4 as uuidv4,
} from 'uuid';
import zlib from 'zlib';

import style from './TemplateBuilder.module.css';
import {
	getSession,
} from 'Vulture/Session.js';

import {
	broncoURL,
	vultureENV,
} from 'Vulture/ENV.js';

import API from 'Vulture/lib/API.js';
import {
	getFDValue,
	matchFDKeys,
	parseEntityName,
} from 'Vulture/FormDataHelpers.js';
import { s3PublicURL } from 'Vulture/S3.js';

const HIDDEN_CATEGORY_NAMES = new Set([
	'delete me please',
	'delete me too',
]);

const HIDDEN_CONFIG_NAMES = new Set(['delete this config']);

const HIDDEN_CONFIG_BY_CATEGORY = {
	'c': new Set(['touch1', 'touch3', 'hybrid3']),
	'default': new Set(['layout1']),
	'healthcare': new Set(['layout1', 'layout2']),
};

class TemplateBuilder extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			activeTemplate:        {},
			category:              {},
			config:                {},
			theme:                 {},
			templateName:          '',
			bucketName:            'ts-theme-editor',
			orientation:           null, // No default - user must select
			latestSkeletonData:    null,
			storeKey:              null,
			searchTerm:            '',
			previewListingsLimit:  60, // Default limit for preview
			includeListingConfigs: false, // Default to not including listing configs
		};

		// temp_preview write serialization (see storeSkeletonDataInTempStore)
		this.tempStoreQueue = Promise.resolve();
		this.tempStoreSeq = 0;
		this.tempStorePendingKey = false;

		this.categoryRef = React.createRef();
		this.configRef = React.createRef();
		this.themeRef = React.createRef();
	}

	ensureFormStateInitialized() {
		// No-op: components now read from fd props directly.
	}

	shouldComponentUpdate(nextProps, nextState) {
		// Prevent unnecessary re-renders when only unrelated props change
		// This significantly improves performance when modals open/close

		// Check if any props changed
		if (
			this.props.accountUUID !== nextProps.accountUUID ||
			this.props.step !== nextProps.step ||
			this.props.pendingUpdates !== nextProps.pendingUpdates ||
			this.props.assignmentMap !== nextProps.assignmentMap ||
			this.props.globalTemplateData !== nextProps.globalTemplateData ||
			this.props.fd !== nextProps.fd
		) {
			return true;
		}

		// Check if state changed
		if (
			this.state.activeTemplate !== nextState.activeTemplate ||
		this.state.category !== nextState.category ||
		this.state.config !== nextState.config ||
		this.state.theme !== nextState.theme ||
		this.state.templateName !== nextState.templateName ||
		this.state.latestSkeletonData !== nextState.latestSkeletonData ||
		this.state.storeKey !== nextState.storeKey ||
		this.state.searchTerm !== nextState.searchTerm ||
		this.state.orientation !== nextState.orientation ||
		this.state.previewListingsLimit !== nextState.previewListingsLimit ||
		this.state.includeListingConfigs !== nextState.includeListingConfigs
		) {
			return true;
		}

		return false;
	}	componentDidUpdate(prevProps) {
		const {
			accountUUID,
			step,
		} = this.props;

		if (prevProps.accountUUID !== accountUUID) {
			this.clearTemplateData();
		}

		if (prevProps.step !== step) {
			this.clearTemplateData();
		}

	}

	clearTemplateData() {
		// refs are not rendered during step 2 & 3.
		if (this.categoryRef.current && this.configRef.current && this.themeRef.current) {
			this.categoryRef.current.select.clearValue();
			this.configRef.current.select.clearValue();
			this.themeRef.current.select.clearValue();
		}

		// DEV-11192: keep SystemProgramming.state.activeEntityKey in sync —
		// when we clear template data here (account/step change), the chat
		// metadata's design context should reset too.
		if (this.props.stateCallback) {
			this.props.stateCallback('activeEntityKey', '');
		}

		this.invalidateTempStoreWrites();
		this.setState({
			activeTemplate:       {},
			category:             {},
			config:               {},
			templateName:         '',
			theme:                {},
			orientation:          null, // Reset to no selection
			latestSkeletonData:   null,
			storeKey:             null,
			previewListingsLimit: 60, // Reset to default
		});

		this.props.setSkeletonHasUpdated();
	}

	// TODO: Do we need this?
	copyTemplate() { }

	/**
	 * Returns true if the template is assigned to any system (pending or saved).
	 * Handles both newVals and orgVals, and ensures form state is initialized.
	 *
	 * Form state initialization is required here because:
	 * - The form's orgVals/rawKeys may not be initialized if the user hasn't interacted with the form yet,
	 *   or if this check is called from a modal context or early in the lifecycle (e.g., Step 1).
	 * - Previously, this logic was only in openDeleteConfirmModal(), but that led to inconsistent behavior
	 *   if deleteTemplate() was called directly (e.g., after modal confirm).
	 * - By moving initialization here, we guarantee correct state for all callers and avoid subtle lifecycle bugs.
	 */
	isTemplateInUse(templateUUID) {
		const { fd, pendingUpdates } = this.props;

		const keys = matchFDKeys(fd, `system.*.template.${templateUUID}`) || [];
		for (const entityKey of keys) {
			const { m1ID: systemUUID, m2ID: parsedTemplateUUID } = parseEntityName(entityKey);

			// Check pendingUpdates for pending assignment changes
			const pendingKey = `system.${systemUUID}.template.${parsedTemplateUUID}`;
			if (typeof pendingUpdates[pendingKey] !== 'undefined') {
				if (pendingUpdates[pendingKey]) {
					return true;
				}
				continue;
			}

			// Check current value in fd
			const cur = getFDValue(fd, entityKey);
			if (cur) {
				return true;
			}
		}
		return false;
	}

	showCannotDeleteModal(systemNames = []) {
		const { app } = this.props;
		const { activeTemplate } = this.state;

		let message;
		if (systemNames.length === 1) {
			message = `Cannot delete '${activeTemplate.display_name}' because it is assigned to '${systemNames[0]}'. Please unassign it first.`;
		} else if (systemNames.length > 1) {
			message = `Cannot delete '${activeTemplate.display_name}' because it is assigned to: ${systemNames.join(', ')}. Please unassign it from all systems first.`;
		} else {
			message = `Cannot delete '${activeTemplate.display_name}' because it is assigned to one or more systems. Please unassign it from all systems first.`;
		}

		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				omitClose: true,
				title:     'Cannot Delete Design',
				jsx:       (
					<div className={`${style.modalContainer} ${style.overflowHidden}`}>
						<div className={style.modalBody}>
							<p>{message}</p>
						</div>
						<div className={`${style.modalActions} ${style.modalActionsPadding}`}>
							<button className='button' onClick={(e) => { e.preventDefault(); app.closeModal(); }}>OK</button>
						</div>
					</div>
				),
			},
		});
	}

	showErrorModal(message) {
		const { app } = this.props;

		app.openModal({
			modalSize:  1,
			showModal:  true,
			modalProps: {
				omitClose: true,
				title:     'Error',
				jsx:       (
					<div className={`${style.modalContainer} ${style.overflowHidden}`}>
						<div className={style.modalBody}>
							<p>{message}</p>
						</div>
						<div className={`${style.modalActions} ${style.modalActionsPadding}`}>
							<button className='button' onClick={(e) => { e.preventDefault(); app.closeModal(); }}>OK</button>
						</div>
					</div>
				),
			},
		});
	}

	async deleteTemplate(skipConfirm = false) {
		const {
			accountUUID, stateCallback, app,
		} = this.props;

		const {
			activeTemplate,
		} = this.state;

		// If caller already showed confirmation modal, skip the built-in confirm.
		if (!skipConfirm && !window.confirm(`Are you sure you want to delete design '${activeTemplate.display_name}'?`)) {
			return;
		}

		// Call API to delete template
		try {
			const sessionKey = getSession().session;
			const response = await fetch(`${broncoURL}/delete/template_entity`, {
				method:  'POST',
				headers: {
					'Content-Type':   'application/json',
					'X-Auth-Session': sessionKey,
				},
				body: JSON.stringify({
					m1:      'template',
					m1_uuid: activeTemplate.uuid,
				}),
			});

			const result = await response.json();

			if (result.error) {
				// Show error modal with system list if template is in use
				const systemNames = (result.payload || []).map((s) => s.system_display_name);
				this.showCannotDeleteModal(systemNames);
				return;
			}

			// Success - refresh template list
			this.clearTemplateData();
			stateCallback('pendingUpdates', {});

			// Fetch templates asynchronously (fetchAccountTemplates has its own loader)
			setTimeout(() => {
				app.fetchAccountTemplates(accountUUID);
			}, 0);
		} catch (err) {
			console.error('Error deleting template:', err);
			this.showErrorModal('Failed to delete design. Please try again.');
		}
	}

	openDeleteConfirmModal() {
		const self = this;
		const { app } = this.props;
		const { activeTemplate } = this.state;

		if (!activeTemplate || !activeTemplate.uuid) {
			app.openModal({
				modalSize: 1,
				showModal: true,
				modalProps: {
					omitClose: true,
					title: 'No Design Selected',
					jsx: (
						<div className={`${style.modalContainer} ${style.overflowHidden}`}>
							<div className={style.modalBody}>
								<p>Please select a design before attempting to delete it.</p>
							</div>
							<div className={`${style.modalActions} ${style.modalActionsPadding}`}>
								<button className='button' onClick={(e) => { e.preventDefault(); app.closeModal(); }}>OK</button>
							</div>
						</div>
					),
				},
			});
			return;
		}

		let dataFetchError = null;
		if (typeof this.props.makeMaps === 'function') {
			try {
				this.props.makeMaps();
			} catch (e) {
				console.error('makeMaps failed:', e);
				dataFetchError = e;
			}
		}

		// If critical data fetching failed, show error modal and abort.
		if (dataFetchError) {
			app.openModal({
				modalSize: 1,
				showModal: true,
				modalProps: {
					omitClose: true,
					title: 'Error Fetching Data',
					jsx: (
						<div className={`${style.modalContainer} ${style.overflowHidden}`}>
							<div className={style.modalBody}>
								<p>
									An error occurred while fetching the latest data required to check template usage.<br />
									Please try again or reload the page.<br />
									<br />
									Error: {dataFetchError.message || String(dataFetchError)}
								</p>
							</div>
							<div className={`${style.modalActions} ${style.modalActionsPadding}`}>
								<button className='button' onClick={(e) => { e.preventDefault(); app.closeModal(); }}>OK</button>
							</div>
						</div>
					),
				},
			});
			return;
		}

		// No need to initialize form state here; isTemplateInUse() now always does it.

		if (this.isTemplateInUse(activeTemplate.uuid)) {
			this.showCannotDeleteModal();
			return;
		}

		app.openModal({
			modalSize: 1,
			showModal: true,
			modalProps: {
				omitClose: true,
				title: 'Delete Design?',
				jsx: (
					<div className={`${style.modalContainer} ${style.overflowHidden}`}>
						<div className={style.modalBody}>
							<p>Are you sure you want to delete design '{activeTemplate.display_name}'?</p>
						</div>
						<div className={`${style.modalActions} ${style.modalActionsDelete}`}>
							<button
								className='button button-outlined'
								onClick={(e) => { e.preventDefault(); app.closeModal(); }}
							>Cancel</button>

							<button
								className={`button-outlined ${style.deleteButton} ${style.deleteButtonSpacing}`}
								onClick={(e) => {
									e.preventDefault();
									app.closeModal();
									self.deleteTemplate(true);
								}}
								title="Delete"
								data-test-id="confirmDeleteDesignButton"
							>Delete</button>
						</div>
					</div>
				),
			},
		});
	}

	stateCallback(key, val) {
		if (typeof key !== undefined && typeof val !== undefined) {
			this.setState({
				[key]: val,
			});
		}

		// Update category, config, and theme if activeTemplate was changed through modal.
		if (key === 'activeTemplate') {
			this.handleActiveTemplateChange(val);
		}
	}

	handleActiveTemplateChange(activeTemplate, isClone, callback) {
		const {
			fd,
			globalTemplateData,
			openModal,
			pendingUpdates,
			assignmentMap,
		} = this.props;

		// Ask user if they want to save changes when switching active templates.
		// Ignore when we're cloning a custom configuration.
		if (Object.keys(pendingUpdates).length > 0 && !isClone) {
			openModal('activeTemplate', activeTemplate, this.stateCallback.bind(this));
			return;
		} else if (Object.keys(assignmentMap).length > 0 && !isClone) {
			openModal('activeTemplate', activeTemplate, this.stateCallback.bind(this));
			return;
		}

		let category = {};
		let config = {};
		let theme = {};

		globalTemplateData.forEach((template_category) => {
			if (template_category.uuid === activeTemplate?.category?.uuid) {
				category = template_category;

				template_category.template_config_list.forEach((categoryToConfig) => {
					let cloneParentUUID;
					// If the selected config isn't global, find the parent it was cloned from.
					if (activeTemplate.config.is_global === false) {
						cloneParentUUID = getFDValue(fd, `template_config.${activeTemplate.config.uuid}.clone_parent_uuid`);
					}

					// If the config owned by a category matches the cloneParentUUID:
					// Modify the config object to use the proper skeleton and preserve the parent themes.
					if (categoryToConfig.template_config.uuid === cloneParentUUID) {
						let templateSkeletonUUID;
						matchFDKeys(fd, `template_config.${activeTemplate.config.uuid}.template_skeleton.*`).forEach((entityKey) => {
							const {
								m2ID,
							} = parseEntityName(entityKey);
							templateSkeletonUUID = m2ID;
						});

						config = categoryToConfig.template_config;
						config = {
							...config,
							clone_parent_uuid:      cloneParentUUID,
							is_global:              false,
							uuid:                   activeTemplate.config.uuid,
							template_skeleton_list: {
								template_config_uuid: activeTemplate.config.uuid,
								template_skeleton:    {
									data: getFDValue(fd, `template_skeleton.${templateSkeletonUUID}.data`),
									hash: getFDValue(fd, `template_skeleton.${templateSkeletonUUID}.hash`),
									uuid: templateSkeletonUUID,
								},
								template_skeleton_uuid: templateSkeletonUUID,
							},
						};

						config.template_theme_list.forEach((configToTheme) => {
							if (configToTheme.template_theme.uuid === activeTemplate?.theme?.uuid) {
								theme = configToTheme.template_theme;
							}
						});

						return;
					}

					if (categoryToConfig.template_config.uuid === activeTemplate?.config?.uuid) {
						config = categoryToConfig.template_config;

						categoryToConfig.template_config.template_theme_list.forEach((configToTheme) => {
							if (configToTheme.template_theme.uuid === activeTemplate?.theme?.uuid) {
								theme = configToTheme.template_theme;
							}
						});
					}
				});
			}
		});

		// If config wasn't found in globalTemplateData (e.g., for non-global account-specific templates),
		// build it from form data
		if (!config.uuid && activeTemplate?.config?.uuid) {
			const configUUID = activeTemplate.config.uuid;
			const categoryUUID = activeTemplate.category.uuid;
			const themeUUID = activeTemplate.theme.uuid;

			if (!category.uuid) {
				category = {
					uuid:         categoryUUID,
					display_name: getFDValue(fd, `template_category.${categoryUUID}.display_name`) || activeTemplate.category.display_name,
				};
			}

			let templateSkeletonUUID;
			matchFDKeys(fd, `template_config.${configUUID}.template_skeleton.*`).forEach((entityKey) => {
				const {
					m2ID,
				} = parseEntityName(entityKey);
				templateSkeletonUUID = m2ID;
			});

			if (templateSkeletonUUID) {
				config = {
					uuid:                   configUUID,
					display_name:           getFDValue(fd, `template_config.${configUUID}.display_name`) || activeTemplate.config.display_name,
					is_global:              getFDValue(fd, `template_config.${configUUID}.is_global`) || false,
					template_skeleton_list: {
						template_config_uuid: configUUID,
						template_skeleton:    {
							data: getFDValue(fd, `template_skeleton.${templateSkeletonUUID}.data`),
							hash: getFDValue(fd, `template_skeleton.${templateSkeletonUUID}.hash`),
							uuid: templateSkeletonUUID,
						},
						template_skeleton_uuid: templateSkeletonUUID,
					},
					template_theme_list: [],
				};

				theme = {
					uuid:         themeUUID,
					display_name: getFDValue(fd, `template_theme.${themeUUID}.display_name`) || activeTemplate.theme.display_name,
				};

				config.template_theme_list.push({
					template_config_uuid: configUUID,
					template_theme:       theme,
					template_theme_uuid:  themeUUID,
				});
			}
		}

		this.setState({
			activeTemplate,
			category,
			config,
			theme,
			templateName: activeTemplate.display_name,
		}, () => {
			// DEV-11192: bubble the selected design up to SystemProgramming so
			// the n8n chat's metadata reflects which design the user is looking
			// at. Without this, chat metadata stays "no design selected" even
			// after a design click. The TLC UUID stays empty until step 4
			// (Systems) populates it via setActiveSystem — that path is
			// preserved.
			if (this.props.stateCallback && activeTemplate?.uuid) {
				this.props.stateCallback('activeEntityKey', `template.${activeTemplate.uuid}`);
			}
			if (callback) {
				callback();
			}
		});
	}

	prepareSkeletonDataForTempStore(currentSkeletonData) {
		return this.buildTempStorePayload(currentSkeletonData, this.state);
	}

	// context carries the design's config/category/theme plus orientation and
	// previewListingsLimit: this.state for the open design, a card's own for another.
	buildTempStorePayload(currentSkeletonData, context) {
		const {
			accountUUID,
		} = this.props;

		const {
			config,
			category,
			theme,
			orientation,
			previewListingsLimit,
		} = context;

		// Default to HZ if no orientation selected
		const finalOrientation = orientation || 'HZ';

		// Find TLC UUID by looking for component with description "top_level_condor"
		let tlcUUID = '';
		Object.keys(currentSkeletonData).forEach((key) => {
			if (key.startsWith('component.')) {
				const component = currentSkeletonData[key];
				if (component && component.description === 'top_level_condor') {
					tlcUUID = key.replace('component.', '');
				}
			}
		});

		// Get current user UUID from session
		const session = getSession();
		const userUUID = session.user || '';

		// Get config UUID (template_config UUID)
		const configUUID = config?.uuid || '';

		// Remove boolean-type listing_config and listing_column_group items from
		// listing_group and listing_collection objects. Booleans are diff-flow
		// relation markers (false = delete); bronco's preview ETL casts these
		// relation values to maps and panics on booleans. Older skeletons saved
		// before cleanSkeletonDataForSaving stripped `false` markers still
		// contain them (e.g. after a listing_config swap).
		const processedSkeletonData = {
			...currentSkeletonData,
		};
		Object.keys(processedSkeletonData).forEach((key) => {
			if (key.startsWith('listing_group.') || key.startsWith('listing_collection.')) {
				const listingObject = processedSkeletonData[key];
				if (listingObject && typeof listingObject === 'object') {
					// Clone before deleting — processedSkeletonData is a shallow copy,
					// so deleting on listingObject directly would mutate the live
					// editor state (latestSkeletonData / TemplateSkeleton flatData).
					const cleanedObject = {
						...listingObject,
					};
					Object.keys(cleanedObject).forEach((configKey) => {
						if ((configKey.startsWith('listing_config.') || configKey.startsWith('listing_column_group.')) && typeof cleanedObject[configKey] === 'boolean') {
							Reflect.deleteProperty(cleanedObject, configKey);
						}
					});
					processedSkeletonData[key] = cleanedObject;
				}
			}
		});

		const accountKey = `account.${accountUUID}`;
		const accountFd = this.props.fd[accountKey] || {};

		const payload = {
			fd: {
				data: {
					[accountKey]: accountFd,
					...processedSkeletonData,
				},
			},
			condor_config: {
				uuid:  config.uuid,
				value: config.display_name,
				name:  'condor_configuration',
			},
			condor_theme: {
				uuid:  theme.uuid,
				value: theme.display_name,
				name:  'condor_theme',
			},
			condor_category: {
				uuid:  category.uuid,
				value: category.display_name,
				name:  'condor_component_group',
			},
			orientation:            finalOrientation, // Send 'HZ' or 'VT' - backend will map to pelican UUID
			preview_listings_limit: (typeof previewListingsLimit === 'number' && previewListingsLimit > 0) ? previewListingsLimit : 60, // Default to 60 if not set or empty
		};

		return {
			payload,
			userUUID,
			tlcUUID,
			configUUID,
		};
	}

	// setStoreKey=false overwrites the deterministic temp_preview entry without
	// adopting its key as this.state.storeKey (see the no-changes branch of
	// updateSkeletonData: a set storeKey makes every card's Preview open THIS design).
	// Writes are chained so they reach the (deterministic) key in call order, and only
	// the most recently queued write may adopt the key: an edit whose POST is still in
	// flight when the user reverts must not land after the revert or re-set storeKey.
	storeSkeletonDataInTempStore(skeletonData, setStoreKey = true) {
		// Build the payload now: it reads config/theme/orientation from state, which a
		// template switch may change before a queued write runs.
		let prepared;
		try {
			prepared = this.prepareSkeletonDataForTempStore(skeletonData);
		} catch (err) {
			return this.tempStoreQueue;
		}
		const {
			payload, userUUID, tlcUUID, configUUID,
		} = prepared;

		const seq = ++this.tempStoreSeq;
		if (setStoreKey) {
			this.tempStorePendingKey = true;
		}

		this.tempStoreQueue = this.tempStoreQueue.then(async () => {
			try {
				const res = await fetch(`${broncoURL}/temp_preview`, {
					method:  'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						data:        payload,
						user_uuid:   userUUID,
						tlc_uuid:    tlcUUID,
						config_uuid: configUUID,
					}),
				});

				if (res.ok && setStoreKey && seq === this.tempStoreSeq) {
					const {
						key,
					} = await res.json();
					if (seq === this.tempStoreSeq) {
						this.tempStorePendingKey = false;
						this.setState({
							storeKey: key,
						});
					}
				}
			} catch (err) {
				// Silently fail - preview will generate new key if needed
			}
		});

		return this.tempStoreQueue;
	}

	// Called wherever storeKey is reset: a write still in flight must not adopt the key
	// of the design being left.
	invalidateTempStoreWrites() {
		this.tempStoreSeq += 1;
		this.tempStorePendingKey = false;
	}

	handleOrientationChange(newOrientation) {
		// Update orientation state and trigger temp_preview update if there's an active template
		this.setState({
			orientation: newOrientation,
		}, () => {
			// Get skeleton data from multiple sources
			const {
				latestSkeletonData,
				config,
			} = this.state;

			let currentSkeletonData = latestSkeletonData;

			// If no latestSkeletonData (template not edited yet), get from config
			if (!currentSkeletonData && config?.template_skeleton_list?.template_skeleton) {
				const skeletonFromConfig = config.template_skeleton_list.template_skeleton.data;
				if (skeletonFromConfig) {
					try {
						// Parse if it's a string
						currentSkeletonData = typeof skeletonFromConfig === 'string' ?
							JSON.parse(skeletonFromConfig) :
							skeletonFromConfig;
					} catch (err) {
						// Silently fail if skeleton data is malformed - preview will regenerate as needed.
						console.error('Failed to parse skeleton data in handleOrientationChange:', err);
					}
				}
			}

			// If we have skeleton data, update temp_preview with new orientation
			if (currentSkeletonData && Object.keys(currentSkeletonData).length > 0) {
				this.storeSkeletonDataInTempStore(currentSkeletonData);
			}
		});
	}

	handlePreviewForTemplate(templateUUID, templateData, isMobile = false) {
		const {
			fd,
			globalTemplateData,
		} = this.props;

		// Build the full template object with category, config, and theme data
		let category = {};
		let config = {};
		let theme = {};

		globalTemplateData.forEach((template_category) => {
			if (template_category.uuid === templateData?.category?.uuid) {
				category = template_category;

				template_category.template_config_list.forEach((categoryToConfig) => {
					let cloneParentUUID;
					if (templateData.config.is_global === false) {
						cloneParentUUID = getFDValue(this.props.fd, `template_config.${templateData.config.uuid}.clone_parent_uuid`);
					}

					if (categoryToConfig.template_config.uuid === cloneParentUUID) {
						let templateSkeletonUUID;
						matchFDKeys(this.props.fd, `template_config.${templateData.config.uuid}.template_skeleton.*`).forEach((entityKey) => {
							const {
								m2ID,
							} = parseEntityName(entityKey);
							templateSkeletonUUID = m2ID;
						});

						config = categoryToConfig.template_config;
						config = {
							...config,
							clone_parent_uuid:      cloneParentUUID,
							is_global:              false,
							uuid:                   templateData.config.uuid,
							template_skeleton_list: {
								template_config_uuid: templateData.config.uuid,
								template_skeleton:    {
									data: getFDValue(this.props.fd, `template_skeleton.${templateSkeletonUUID}.data`),
									hash: getFDValue(this.props.fd, `template_skeleton.${templateSkeletonUUID}.hash`),
									uuid: templateSkeletonUUID,
								},
								template_skeleton_uuid: templateSkeletonUUID,
							},
						};

						config.template_theme_list.forEach((configToTheme) => {
							if (configToTheme.template_theme.uuid === templateData?.theme?.uuid) {
								theme = configToTheme.template_theme;
							}
						});

						return;
					}

					if (categoryToConfig.template_config.uuid === templateData?.config?.uuid) {
						config = categoryToConfig.template_config;

						categoryToConfig.template_config.template_theme_list.forEach((configToTheme) => {
							if (configToTheme.template_theme.uuid === templateData?.theme?.uuid) {
								theme = configToTheme.template_theme;
							}
						});
					}
				});
			}
		});

		// FALLBACK: If config wasn't found in globalTemplateData (e.g., for non-global account-specific templates),
		// build it from form data - this mirrors the logic in handleActiveTemplateChange
		if (!config.uuid && templateData?.config?.uuid) {
			const configUUID = templateData.config.uuid;
			const categoryUUID = templateData.category.uuid;
			const themeUUID = templateData.theme.uuid;

			if (!category.uuid) {
				category = {
					uuid:         categoryUUID,
					display_name: getFDValue(this.props.fd, `template_category.${categoryUUID}.display_name`) || templateData.category.display_name,
				};
			}

			let templateSkeletonUUID;
			matchFDKeys(this.props.fd, `template_config.${configUUID}.template_skeleton.*`).forEach((entityKey) => {
				const {
					m2ID,
				} = parseEntityName(entityKey);
				templateSkeletonUUID = m2ID;
			});

			if (templateSkeletonUUID) {
				config = {
					uuid:                   configUUID,
					display_name:           getFDValue(this.props.fd, `template_config.${configUUID}.display_name`) || templateData.config.display_name,
					is_global:              getFDValue(this.props.fd, `template_config.${configUUID}.is_global`) || false,
					template_skeleton_list: {
						template_config_uuid: configUUID,
						template_skeleton:    {
							data: getFDValue(this.props.fd, `template_skeleton.${templateSkeletonUUID}.data`),
							hash: getFDValue(this.props.fd, `template_skeleton.${templateSkeletonUUID}.hash`),
							uuid: templateSkeletonUUID,
						},
						template_skeleton_uuid: templateSkeletonUUID,
					},
					template_theme_list: [],
				};

				theme = {
					uuid:         themeUUID,
					display_name: getFDValue(this.props.fd, `template_theme.${themeUUID}.display_name`) || templateData.theme.display_name,
				};

				config.template_theme_list.push({
					template_config_uuid: configUUID,
					template_theme:       theme,
					template_theme_uuid:  themeUUID,
				});
			}
		}

		// Validate that we have all required data before attempting preview
		if (!config.uuid || !config.template_skeleton_list?.template_skeleton?.uuid) {
			alert('Unable to preview template: missing configuration or skeleton data.');
			return;
		}

		return this.previewTemplateCard({
			config,
			category,
			theme,
			orientation:          this.state.orientation,
			previewListingsLimit: this.state.previewListingsLimit,
		}, isMobile);
	}

	handleEditTemplate(templateUUID, templateData) {
		// Set the active template with full data, then open modal in callback
		this.handleActiveTemplateChange({
			...templateData,
			uuid: templateUUID,
		}, false, () => {
			// Open modal after state is updated
			this.openNewDesignModal();
		});
	}

	handleCopyTemplate(templateUUID, templateData) {
		this.openCopyTemplateModal(templateUUID, templateData);
	}

	// Previews the open design. Its unsaved edits live in storeKey / latestSkeletonData.
	handlePreview = async (isMobile = false) => {
		const {
			storeKey,
			latestSkeletonData,
			config,
		} = this.state;

		// If we have a stored key from updateSkeletonData, use it directly
		if (storeKey) {
			window.open(`${this.previewHost(isMobile)}/?store_key=${storeKey}`, '_blank');
			return;
		}

		const currentSkeletonData = latestSkeletonData || this.savedSkeletonForConfig(config);

		return this.openTempPreview(currentSkeletonData, this.state, isMobile);
	};

	// Previews another design's card from its own saved skeleton. Never routed through
	// state: swapping the card into state re-renders the open editor with the card's
	// skeleton, which TemplateSkeleton reports back as an edit (clearing pendingUpdates,
	// or overwriting the open design's temp_preview).
	previewTemplateCard(context, isMobile = false) {
		return this.openTempPreview(this.savedSkeletonForConfig(context.config), context, isMobile);
	}

	previewHost(isMobile) {
		return isMobile ? window._getEnv('CONDOR_MOBILE_URL') : window._getEnv('PREVIEW_URL');
	}

	savedSkeletonForConfig(config) {
		const skeleton = config?.template_skeleton_list?.template_skeleton;
		if (!skeleton?.uuid) {
			return {};
		}

		const formSkeletonData = getFDValue(this.props.fd, `template_skeleton.${skeleton.uuid}.data`);
		if (formSkeletonData) {
			try {
				return JSON.parse(formSkeletonData);
			} catch (err) {
				// Fall back to the config's skeleton data below
			}
		}

		if (!skeleton.data) {
			return {};
		}

		try {
			return JSON.parse(skeleton.data);
		} catch (err) {
			return {};
		}
	}

	// Stores skeletonData in temp_store under context's config/category/theme and opens
	// the preview.
	async openTempPreview(skeletonData, context, isMobile) {
		if (Object.keys(skeletonData).length > 0) {
			try {
				const {
					payload, userUUID, tlcUUID, configUUID,
				} = this.buildTempStorePayload(skeletonData, context);

				const res = await fetch(`${broncoURL}/temp_preview`, {
					method:  'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						data:        payload,
						user_uuid:   userUUID,
						tlc_uuid:    tlcUUID,
						config_uuid: configUUID,
					}),
				});

				if (res.ok) {
					const {
						key,
					} = await res.json();
					window.open(`${this.previewHost(isMobile)}/?store_key=${key}`, '_blank');
					return;
				}
			} catch (err) {
				// Fall through to error
			}
		}

		alert('No preview data available. Please select a template first.');
	}


	async saveTemplate(draft = this.state) {
		const {
			activeTemplate, category, config, theme, templateName,
		} = draft;
		const {
			accountUUID,
			app,
			fd,
		} = this.props;

		const isCreate = !activeTemplate.uuid;

		if (!templateName?.trim() || (isCreate && (!category.uuid || !config.uuid || !theme.uuid))) {
			alert('Please fill out all fields.');
			return false;
		}

		const categoryChanged = (category.uuid || '') !== (activeTemplate.category?.uuid || '');
		const configChanged = (config.uuid || '') !== (activeTemplate.config?.uuid || '');

		if (!isCreate && (
			(categoryChanged && (!config.uuid || !theme.uuid)) ||
			(configChanged && !theme.uuid)
		)) {
			alert('Please fill out all fields.');
			return false;
		}

		if (this._savingTemplate) {
			return false;
		}
		this._savingTemplate = true;

		const actions = [];

		if (isCreate) {
			actions.push({
				op:   'create',
				m1:   'template',
				ref:  'tpl',
				body: {
					uuid:         uuidv4(),
					display_name: templateName,
				},
			});
			actions.push({
				op:      'create_map',
				m1:      'account',
				uuid:    accountUUID,
				m2:      'template',
				m2_uuid: '$tpl',
			});
		} else if (templateName !== activeTemplate.display_name) {
			actions.push({
				op:   'update',
				m1:   'template',
				uuid: activeTemplate.uuid,
				body: {
					display_name: templateName,
				},
			});
		}

		const templateRef = isCreate ? '$tpl' : activeTemplate.uuid;

		[
			[ 'template_category', category, activeTemplate.category, ],
			[ 'template_config', config, activeTemplate.config, ],
			[ 'template_theme', theme, activeTemplate.theme, ],
		].forEach(([ m2, next, prev, ]) => {
			const prevUUID = prev?.uuid || '';
			const nextUUID = next?.uuid || '';

			if (prevUUID === nextUUID) {
				return;
			}

			if (!nextUUID) {
				return;
			}

			const prevIsPersisted = Boolean(
				getFDValue(fd, `template.${activeTemplate.uuid}.${m2}.${prevUUID}`)
			);

			if (prevUUID && prevIsPersisted) {
				actions.push({
					op:      'delete_map',
					m1:      'template',
					uuid:    templateRef,
					m2,
					m2_uuid: prevUUID,
				});
			}
			actions.push({
				op:      'create_map',
				m1:      'template',
				uuid:    templateRef,
				m2,
				m2_uuid: nextUUID,
			});
		});

		if (actions.length > 0) {
			try {
				await API.batch(actions, accountUUID);
			} catch (err) {
				alert(`Save failed: ${err?.body?.error || err?.message || 'A network error has occurred.'}`);
				this._savingTemplate = false;
				return false;
			}
		}

		this._savingTemplate = false;
		this.clearTemplateData();

		if (actions.length > 0) {
			setTimeout(() => {
				app.fetchAccountTemplates(accountUUID);
			}, 0);
		}

		return true;
	}

	cleanSkeletonDataForSaving(skeletonData) {
		// Clean skeleton data by removing virtual VCL provider entities
		// These are dynamically generated at runtime and shouldn't be persisted
		return Object.entries(skeletonData).reduce((acc, [ entityKey, entityData, ]) => {
			// Skip if this is the entity data itself
			if (typeof entityData !== 'object' || entityData === null) {
				acc[entityKey] = entityData;
				return acc;
			}
			// Clean the entity's children
			const cleanedEntity = Object.entries(entityData).reduce((childAcc, [ childKey, childValue, ]) => {
				// Remove virtual VCL provider children (they start with 'visual_content_loop_')
				if (childKey.startsWith('visual_content_loop_')) {
					// Skip - don't persist virtual VCL providers
					return childAcc;
				}
				// Remove `false` relation markers (entity-type keys set to false).
				// They are deletion instructions for the /w/edit diff flow, but a
				// template skeleton is saved as a whole JSON blob — a persisted
				// `false` is junk that panics bronco's preview ETL, which casts
				// every relation value to a map (e.g. listing_column_group.* on
				// listing_group after a listing_config swap).
				if (childKey.includes('.') && childValue === false) {
					return childAcc;
				}
				// Keep everything else
				childAcc[childKey] = childValue;
				return childAcc;
			}, {});
			acc[entityKey] = cleanedEntity;
			return acc;
		}, {});
	}

	cloneConfig(parentPendingUpdates) {
		const {
			activeTemplate, config,
		} = this.state;

		const url = `${broncoURL}/w/clone/template_config`;
		const sessionKey = getSession().session;

		return new Promise((resolve, reject) => {
			new kali({
				body: {
					'data': [
						config.uuid,
					],
					'exclusions': [
						// Removed 'setting' and 'setting_type' exclusions
						// so that VCL settings (visual_content_loop_id, is_visual_content_loop)
						// are preserved during clone
					],
				},
				headers: {
					'content_type':   'application/json',
					'X-Auth-Session': sessionKey,
				},
				method: 'POST',
			}).post(url, {
				success: (_kali, res, contents) => {
					if (contents && contents.data && contents.keys) {
					// Find the cloned template_config UUID.
						Object.values(contents.keys).forEach((cloneKeyArr) => {
							if (Array.isArray(cloneKeyArr)) {
								const cloneConfigKey = cloneKeyArr[0];
								const cloneConfigUUID = cloneConfigKey.split('.')[1];

								let cloneSkeletonUUID = '';

								// Find the cloned template_skeleton UUID to update its data and hash.
								Object.keys(contents.data).forEach((entityKey) => {
									if (entityKey.startsWith('template_skeleton.')) {
										cloneSkeletonUUID = entityKey.split('.')[1];
									}
								});

								const {
									newSkeletonData,
								} = parentPendingUpdates;

								const pendingUpdates = {
								// Set the cloned template_config.is_global to false.
									[`template_config.${cloneConfigUUID}.is_global`]: false,

									// Satisfy props.value and avoid needing a refresh.
									[`template_config.${cloneConfigUUID}.display_name`]:                           config.display_name,
									[`template_config.${cloneConfigUUID}.clone_parent_uuid`]:                      config.uuid,
									[`template_config.${cloneConfigUUID}.template_skeleton.${cloneSkeletonUUID}`]: true,

									// Update template_to_template_config_map.
									[`template.${activeTemplate.uuid}.template_config.${cloneConfigUUID}`]: true,
									[`template.${activeTemplate.uuid}.template_config.${config.uuid}`]:     false,

									// Update template_skeleton data and hash.
									[`template_skeleton.${cloneSkeletonUUID}.data`]: JSON.stringify(newSkeletonData),
									[`template_skeleton.${cloneSkeletonUUID}.hash`]: md5(JSON.stringify(newSkeletonData)),
								};

								// Pass pending updates up to SystemProgramming
								this.props.stateCallback('pendingUpdates', pendingUpdates);

								this.props.makeMaps();

								// Update the activeTemplate
								this.handleActiveTemplateChange({
									...activeTemplate,
									config: {
										...activeTemplate.config,
										is_global: false,
										uuid:      cloneConfigUUID,
									},
								}, true);

								resolve(pendingUpdates);
							}

							reject('Invalid response.keys from /w/clone: ', contents);
						});
					}

					reject('Invalid response from /w/clone: ', contents);
				},

				failure: (_kali, err) => {
					reject(err);
				},
			});
		});
	}

	updateSkeletonData(newSkeletonData) {
		const {
			stateCallback,
		} = this.props;

		const {
			activeTemplate,
			config,
		} = this.state;

		// Safety check: ensure template_skeleton relationship is loaded
		if (!config?.template_skeleton_list?.template_skeleton) {
			console.warn('updateSkeletonData called but template_skeleton relationship not loaded yet');
			return;
		}

		const {
			data: skeletonData,
			uuid: skeletonUUID,
		} = config.template_skeleton_list.template_skeleton;

		// Clean the skeleton data before comparing/saving
		// This removes virtual VCL provider entities that are generated at runtime
		const cleanedSkeletonData = this.cleanSkeletonDataForSaving(newSkeletonData);

		// If newSkeletonData doesn't match the active config's skeleton data, save the changes.
		if (JSON.stringify(cleanedSkeletonData) !== skeletonData) {
			// Store the latest skeleton data in component state for handlePreview to use
			// Use the UNCLEANED data for preview so VCL providers appear correctly
			this.setState({
				latestSkeletonData: newSkeletonData,
			});

			// Store the UNCLEANED data in temp_store asynchronously for preview functionality
			this.storeSkeletonDataInTempStore(newSkeletonData);

			// If the config is global and has been modified, we need to clone the config for the template.
			// Use config.is_global from state (populated from globalTemplateData) rather than
			// activeTemplate.config.is_global (from templateRenderMap) which can be undefined
			// when form data isn't loaded for global configs.
			if (config.is_global) {
				stateCallback('cb', this.cloneConfig.bind(this));
				stateCallback('pendingUpdates', {
					newSkeletonData: cleanedSkeletonData, // Use cleaned data for saving
				});

				return;
			}

			let pendingUpdates = {
				[`template_skeleton.${skeletonUUID}.data`]: JSON.stringify(cleanedSkeletonData),
				[`template_skeleton.${skeletonUUID}.hash`]: md5(JSON.stringify(cleanedSkeletonData)),
			};

			// Trigger Save button in SystemProgramming
			stateCallback('pendingUpdates', pendingUpdates);

			return;
		}

		// Clear the latest skeleton data and store key since no changes were made.
		// tempStorePendingKey covers an edit write still in flight (storeKey not set yet).
		const hadPreviewKey = this.state.storeKey !== null || this.tempStorePendingKey;
		this.invalidateTempStoreWrites();
		this.setState({
			latestSkeletonData: null,
			storeKey:           null,
		});

		// The temp_preview key is deterministic per user/tlc/config, so an already-open
		// preview tab re-reads it on refresh. Overwrite it with the reverted skeleton,
		// otherwise the tab keeps rendering the abandoned edit (e.g. a removed icon).
		// Only when a preview key existed (an edit was previewed): this branch also runs
		// on plain open/save, and adopting a key there would make other cards' Preview
		// open this design. storeKey stays null either way.
		if (hadPreviewKey) {
			this.storeSkeletonDataInTempStore(newSkeletonData, false);
		}

		// Disable Save button in SystemProgramming
		stateCallback('pendingUpdates', {});
	}

	findDisplayName(m1UUID) {
		const {
			globalTemplateData,
		} = this.props;

		// for...of will stop looping when return is hit.
		for (const category of globalTemplateData) {
			if (category.uuid === m1UUID) {
				return category.display_name;
			}

			if (category.template_config_list) {
				for (const configMapModel of category.template_config_list) {
					const {
						template_config = {},
					} = configMapModel;
					if (template_config.uuid === m1UUID) {
						return template_config.display_name;
					}

					if (template_config.template_theme_list) {
						for (const themeMapModel of template_config.template_theme_list) {
							const {
								template_theme = {},
							} = themeMapModel;
							if (template_theme.uuid === m1UUID) {
								return template_theme.display_name;
							}
						}
					}
				}
			}
		}
	}

	/**
	 * Finds the is_global value for a template_config from globalTemplateData.
	 * This is used as a fallback when getFDValue(this.props.fd,) returns undefined
	 * (which happens for global configs not loaded into form state).
	 *
	 * @param {string} configUUID - The UUID of the template_config to look up
	 * @returns {boolean|undefined} - true if global config found, undefined if not found
	 */
	findConfigIsGlobal(configUUID) {
		const {
			globalTemplateData,
		} = this.props;

		for (const category of globalTemplateData) {
			if (category.template_config_list) {
				for (const configMapModel of category.template_config_list) {
					const {
						template_config = {},
					} = configMapModel;
					if (template_config.uuid === configUUID) {
						return template_config.is_global;
					}
				}
			}
		}

		return undefined;
	}

	renderTemplates() {
		const {
			accountUUID, form, value,
		} = this.props;
		const {
			activeTemplate,
			searchTerm,
		} = this.state;

		let templateRenderMap = {};
		matchFDKeys(this.props.fd, `account.${accountUUID}.template.*`).forEach((entityKey) => {
			const {
				m2ID,
			} = parseEntityName(entityKey);

			// Skip templates that are pending delete.
			if (getFDValue(this.props.fd, `account.${accountUUID}.template.${m2ID}`) === false) {
				return;
			}

			const display_name = getFDValue(this.props.fd, `template.${m2ID}.display_name`);

			// Skip templates that were never saved.
			if (!display_name) {
				return;
			}

			templateRenderMap[m2ID] = {
				display_name,
			};
		});

		Object.keys(templateRenderMap).forEach((templateUUID) => {
		// OTO
			matchFDKeys(this.props.fd, `template.${templateUUID}.template_category.*`).forEach((entityKey) => {
				const {
					m2ID: categoryUUID,
				} = parseEntityName(entityKey);
				let display_name = getFDValue(this.props.fd, `template_category.${categoryUUID}.display_name`);

				if (!display_name) {
					display_name = this.findDisplayName(categoryUUID);
				}

				// Add data to the render map.
				templateRenderMap[templateUUID] = {
					...templateRenderMap[templateUUID],
					category: {
						display_name,
						uuid: categoryUUID,
					},
				};
			});

			// OTO
			matchFDKeys(this.props.fd, `template.${templateUUID}.template_config.*`).forEach((entityKey) => {
				const {
					m2ID: configUUID,
				} = parseEntityName(entityKey);
				let display_name = getFDValue(this.props.fd, `template_config.${configUUID}.display_name`);
				// Try to get is_global from form state first, then fallback to globalTemplateData
				// This fixes the bug where global configs aren't loaded into form state,
				// causing is_global to be undefined and skipping the clone check
				let is_global = getFDValue(this.props.fd, `template_config.${configUUID}.is_global`);
				if (is_global === undefined) {
					is_global = this.findConfigIsGlobal(configUUID);
				}

				if (!display_name) {
					display_name = this.findDisplayName(configUUID);
				}

				templateRenderMap[templateUUID] = {
					...templateRenderMap[templateUUID],
					config: {
						display_name,
						is_global,
						uuid: configUUID,
					},
				};
			});

			// OTO
			matchFDKeys(this.props.fd, `template.${templateUUID}.template_theme.*`).forEach((entityKey) => {
				const {
					m2ID: themeUUID,
				} = parseEntityName(entityKey);
				let display_name = getFDValue(this.props.fd, `template_theme.${themeUUID}.display_name`);

				if (!display_name) {
					display_name = this.findDisplayName(themeUUID);
				}

				templateRenderMap[templateUUID] = {
					...templateRenderMap[templateUUID],
					theme: {
						display_name,
						uuid: themeUUID,
					},
				};
			});
		});

		return Object.entries(templateRenderMap)
			.filter(([ templateUUID, templateData, ]) => {
				// Filter out templates with incomplete data (likely stale/deleted references)
				if (!templateData?.category || !templateData?.config || !templateData?.theme) {
					return false;
				}

				// Filter by search term - searches across name, category, config, and theme
				if (searchTerm) {
					const searchLower = searchTerm.toLowerCase();
					const nameMatch = (templateData.display_name || '').toLowerCase().includes(searchLower);
					const categoryMatch = (templateData?.category?.display_name || '').toLowerCase().includes(searchLower);
					const configMatch = (templateData?.config?.display_name || '').toLowerCase().includes(searchLower);
					const themeMatch = (templateData?.theme?.display_name || '').toLowerCase().includes(searchLower);

					if (!nameMatch && !categoryMatch && !configMatch && !themeMatch) {
						return false;
					}
				}

				return true;
			})
			.sort(([ , templateDataA, ], [ , templateDataB, ]) => {
				if ((templateDataA?.display_name || '').toLowerCase() > (templateDataB?.display_name || '').toLowerCase()) {
					return 1;
				}
				return -1;
			})
			.map(([ templateUUID, templateData, ], i) => {
				const isOpenDesign = activeTemplate.uuid === templateUUID;
				let active = '';
				if (isOpenDesign) {
					active = style.templateActive;
				}

				let customIcon = null;
				if (templateData?.config?.is_global === false) {
					customIcon = <CustomIcon icon='code' color='#C93A6A' />;
				}

				return (
					<div
						data-test-id={`templateCard-${templateUUID}`}
						id={`tb_template_${templateUUID}`}
						className={`${style.template} ${active}`}
						key={i}
						onClick={(e) => {
							e.preventDefault();
							const activeTemplate = {
								...templateData,
								uuid: templateUUID,
							};
							this.handleActiveTemplateChange(activeTemplate);
						}}
					>
						<div className={style.templateIcons}>
							<div
								className={style.templateIcon}
								onClick={(e) => {
									e.stopPropagation();
									if (isOpenDesign) {
										this.handlePreview();
									} else {
										this.handlePreviewForTemplate(templateUUID, templateData);
									}
								}}
								title="Preview"
								data-test-id="templatePreviewButton"
							>
								<CustomIcon icon='eye' color='#4BA046' />
							</div>
							<div
								className={style.condorPreviewIcon}
								onClick={(e) => {
									e.stopPropagation();
									if (isOpenDesign) {
										this.handlePreview(true);
									} else {
										this.handlePreviewForTemplate(templateUUID, templateData, true);
									}
								}}
								title="Mobile Preview"
								data-test-id="templateMobilePreviewButton"
							>
								<CustomIcon icon="mobile-alt" color="#4BA046" />
							</div>
							<div
								className={style.templateIcon}
								onClick={(e) => {
									e.stopPropagation();
									this.handleEditTemplate(templateUUID, templateData);
								}}
								title="Edit"
								data-test-id="templateEditButton"
							>
								<CustomIcon icon='edit' color='#3b82f6' />
							</div>
							<div
								className={style.templateIcon}
								onClick={(e) => {
									e.stopPropagation();
									this.handleCopyTemplate(templateUUID, templateData);
								}}
								title="Copy"
								data-test-id="templateCopyButton"
							>
								<CustomIcon icon='copy' color='#3b82f6' />
							</div>
						</div>
						<div className={style.templateName} data-test-id="templateName">{templateData.display_name}</div>
						<div className={style.templateData}>
							<div className={style.templateDataKey}>Category:</div>
							<div className={style.templateDataValue}>{templateData?.category?.display_name || 'BAD_CATEGORY'}</div>
						</div>
						<div className={style.templateData}>
							<div className={style.templateDataKey}>Configuration:</div>
							<div className={style.templateDataValue}>{customIcon} {templateData?.config?.display_name || 'BAD_CONFIG'}</div>
						</div>
						<div className={style.templateData}>
							<div className={style.templateDataKey}>Theme:</div>
							<div className={style.templateDataValue}>{templateData?.theme?.display_name || 'BAD_THEME'}</div>
						</div>
					</div>
				);
			});
	}

	renderStepTwo() {
		const {
			activeTemplate, config,
		} = this.state;

		// Don't show the skeleton unless a template is active and has proper config
		if (Object.keys(activeTemplate).length === 0 || !config?.uuid) {
			return '';
		}

		let existingFlatData = null;
		if (config?.template_skeleton_list?.template_skeleton) {
			try {
				let data = config.template_skeleton_list.template_skeleton.data;
				
				// Check if data is compressed (base64-encoded gzip, starts with H4sI)
				if (typeof data === 'string' && (data.startsWith('H4sI') || (data.length > 0 && data[0] !== '{'))) {
					// Data appears to be compressed, decompress it
					try {
						const compressedBuffer = Buffer.from(data, 'base64');
						const decompressedBuffer = zlib.gunzipSync(compressedBuffer);
						data = decompressedBuffer.toString('utf8');
						
						// If decompressed data is JSON string wrapped in quotes, unwrap it
						if (data.startsWith('"') && data.endsWith('"')) {
							data = JSON.parse(data);
						}
					} catch (decompressErr) {
						console.error('Error decompressing skeleton data:', decompressErr);
						// If decompression fails, try parsing as-is
					}
				}
				
				existingFlatData = JSON.parse(data);
			} catch (err) {
				console.error('Error parsing skeleton data: ', err);
			}
		}

		// If template_config.is_global is false, customConfig is true. Fallback to false if not found.
		let customConfig = !config.is_global || false;

		// Use config UUID as key to force React to remount TemplateSkeleton when switching templates.
		// This ensures the component gets fresh state instead of trying to update with stale data.
		const skeletonKey = config?.uuid || 'no-config';

		return (
			<TemplateSkeleton
				key={skeletonKey}
				app={this.props.app}
				accountUUID={this.props.accountUUID}
				customConfig={customConfig}
				existingFlatData={existingFlatData}
				stateCallback={this.props.stateCallback}
				pendingUpdates={this.props.pendingUpdates}
				form={this.props.form}
				config={this.state.config}
				category={this.state.category}
				theme={this.state.theme}
				orientation={this.state.orientation}
				updateSkeletonData={this.updateSkeletonData.bind(this)}
				handlePreview={this.handlePreview.bind(this)}
				step={this.props.step}
				settingMap={this.props.settingMap}
				fd={this.props.fd}
				setOrientation={(orientation) => {
					this.handleOrientationChange(orientation);
				}}
			/>
		);
	}

	modifySkeletonUUIDs(skeletonData) {
		const {
			form,
		} = this.props;

		Object.keys(skeletonData).forEach((m1Key) => {
			const {
				m1ID,
			} = parseEntityName(m1Key);

			// Find invalid m1UUIDs and replace m1Key with a new UUID.
			if (m1ID.length !== 36) {
				const newM1ID = uuidv4();
				const newM1Key = m1Key.replace(m1ID, newM1ID);

				skeletonData[newM1Key] = skeletonData[m1Key];
				delete skeletonData[m1Key];

				// Loop over skeletonData to find if the m1 was used as an m2 anywhere.
				// Replace the m2ID the same new UUID from above if so.
				Object.entries(skeletonData).forEach(([ entityKey, m2Obj, ]) => {
					for (const m2Key of Object.keys(m2Obj)) {
						const {
							m1ID: childUUID,
						} = parseEntityName(m2Key);

						if (childUUID === m1ID) {
							const newM2Key = m2Key.replace(childUUID, newM1ID);

							skeletonData[entityKey][newM2Key] = skeletonData[entityKey][m2Key];
							delete skeletonData[entityKey][m2Key];

							break;
						}
					}
				});
			}
		});

		return skeletonData;
	}

	handleSystemAssignment(systemUUID, status, systemName, currentTemplateUUID) {
		const {
			stateCallback,
		} = this.props;

		const {
			activeTemplate,
		} = this.state;

		if (!activeTemplate.uuid) {
			return;
		}

		const {
			assignmentMap,
		} = this.props;

		// Create a new assignmentMap object to trigger React re-render
		let newAssignmentMap = {
			...assignmentMap,
		};

		// Remove any previously existing assignments for this system. A template
		// switch queues two entries (old = false, new = true), so clear every
		// pending entry for the system — leaving the old = false entry behind
		// would silently un-assign the template on save.
		let prevUpdates = false;
		Object.keys(newAssignmentMap).forEach((key) => {
			if (key.startsWith(`system.${systemUUID}.`)) {
				prevUpdates = true;

				Reflect.deleteProperty(newAssignmentMap, key);
			}
		});

		if (prevUpdates) {
		// Trigger Save button in SystemProgramming
			stateCallback('assignmentMap', newAssignmentMap);

			// Remove the system from the deleteAllMap
			return;
		}

		// Prompt the user if they're removing a template from a system.
		if (status === 'enabled') {
			if (!window.confirm(`Are you sure you want to remove ${activeTemplate.display_name} from ${systemName}?`)) {
				return;
			}

			newAssignmentMap[`system.${systemUUID}.template.${activeTemplate.uuid}`] = false;

			// Trigger Save button in SystemProgramming
			stateCallback('assignmentMap', newAssignmentMap);

			return;
		}

		// Prompt the user if they're updating a system that has a different template.
		if (status === 'disabled') {
			if (!window.confirm(`Are you sure you want to update ${systemName} to use ${activeTemplate.display_name}?`)) {
				return;
			}

			// Un-assign the system's current template in the same request so the
			// backend clears the old component tree — otherwise the system ends up
			// with two live trees and the player can render the wrong one.
			if (currentTemplateUUID && currentTemplateUUID !== activeTemplate.uuid) {
				newAssignmentMap[`system.${systemUUID}.template.${currentTemplateUUID}`] = false;
			}
		}

		newAssignmentMap[`system.${systemUUID}.template.${activeTemplate.uuid}`] = true;

		// Trigger Save button in SystemProgramming
		stateCallback('assignmentMap', newAssignmentMap);
	}

	// Create a render map of all system_groups under the selected account, that own one or more systems.
	buildRenderMap(m1, m1UUID, renderMap = {}) {
		matchFDKeys(this.props.fd, `${m1}.${m1UUID}.system_group.*`).forEach((entityKey) => {
			const {
				m2ID: systemGroupUUID,
			} = parseEntityName(entityKey);

			// Recursively loop to find nested systems.
			this.buildRenderMap('system_group', systemGroupUUID, renderMap);

			// Find all systems owned by the system group.
			matchFDKeys(this.props.fd, `system_group.${systemGroupUUID}.system.*`).forEach((entityKey) => {
				const {
					m2ID: systemUUID,
				} = parseEntityName(entityKey);

				const fd = this.props.fd;
				const systemGroupName = getFDValue(fd, `system_group.${systemGroupUUID}.display_name`);
				const systemName = getFDValue(fd, `system.${systemUUID}.display_name`) || (fd[`system.${systemUUID}`] && fd[`system.${systemUUID}`]['display_name']);
				const resolution = getFDValue(fd, `system.${systemUUID}.resolution`) || (fd[`system.${systemUUID}`] && fd[`system.${systemUUID}`]['resolution']);

				let templateName = '';
				let templateUUID = '';

				// Find the system_to_template relation and grab the template.display_name. OTO
				matchFDKeys(fd, `system.${systemUUID}.template.*`).forEach((entityKey) => {
					const {
						m2ID: _templateUUID,
					} = parseEntityName(entityKey);

					// Check for pending system.*.template.* deletions before assigning the template name.
					if (getFDValue(fd, `system.${systemUUID}.template.${_templateUUID}`)) {
						templateName = getFDValue(fd, `template.${_templateUUID}.display_name`);
						templateUUID = _templateUUID;
					}

					// Check for previously stored values in fd
					if (fd[`template.${_templateUUID}`]) {
						templateName = fd[`template.${_templateUUID}`]['display_name'];
						templateUUID = _templateUUID;
					}

					// Check to see if the relation is about to be removed via pendingUpdates
					const pendingKey = `system.${systemUUID}.template.${_templateUUID}`;
					if (this.props.pendingUpdates[pendingKey] === false) {
						templateName = '';
						templateUUID = '';
					}
				});

				renderMap[systemGroupName] = {
					...renderMap[systemGroupName],
					[systemUUID]: {
						systemName,
						resolution,
						templateName,
						templateUUID,
					},
				};
			});
		});

		return renderMap;
	}

	renderStepThree() {
		const {
			assignmentMap,
			accountUUID,
			fd,
		} = this.props;

		const {
			activeTemplate,
		} = this.state;

		let groupedSystems = [];
		for (const [ m1Key, m1Data, ] of Object.entries(fd)) {
			if (m1Key.startsWith('component_group.')) {
				for (const m2Key of Object.keys(m1Data)) {
					if (m2Key.startsWith('system.')) {
						groupedSystems.push(m2Key);
					}
				}
			}
		}
		groupedSystems = [ ...new Set(groupedSystems), ];

		const renderMap = this.buildRenderMap('account', accountUUID);
		let sortedEntries = Object.entries(renderMap).sort((a, b) => {
			const labelA = a[0].toLowerCase();
			const labelB = b[0].toLowerCase();
			if (labelA > labelB) {
				return 1;
			}
			return -1;
		});
		return sortedEntries.map(([ systemGroupName, systemObj, ], i) => {
			let sortedSystems = Object.entries(systemObj).sort((a, b) => {
				if (a[1].systemName > b[1].systemName) {
					return 1;
				}
				return -1;
			});
			return (
				<div key={i}>
					<div>{systemGroupName}</div>
					{sortedSystems.map(([ systemUUID, systemVals, ]) => {
						const {
							systemName,
							resolution,
							templateName,
							templateUUID,
						} = systemVals;

						let templateNameDisplay = templateName;

						let orientationIcon;
						let highResIcon;
						let isActive;
						let isGrouped = groupedSystems.includes(`system.${systemUUID}`);

						if (resolution) {
							const [ width, height, ] = resolution.split('x');

							if (width > height) {
								orientationIcon = <CustomIcon icon={'horizontal'} />;
							}

							if (height > width) {
								orientationIcon = <CustomIcon icon={'vertical'} />;
							}

							// TODO: Expand this for 5k, 10k, etc. future systems.
							if (height > 3000 || width > 3000) {
								highResIcon = <span className={style.resolutionIcon}><CustomIcon icon='4k' /></span>;
							}
						}

						let activeIcon = <CustomIcon icon='ban' color='#C7567C' />;
						let containerStyle = style.systemDisabled;
						let status = 'disabled';
						let pendingChangeStyle;

						// If there is no active template selected or if there is no template tied to the system, use the unassigned style.
						if (!activeTemplate.uuid || !templateUUID) {
							isActive = false;
							activeIcon = <CustomIcon icon='circle' color='#DBDBDB' />;
							containerStyle = style.systemEmpty;
							status = 'empty';
						}

						// If there is a template name and the display_names match, set the system to active.
						// Else the names do not match, use the inUse style.
						if (templateUUID && templateUUID === activeTemplate.uuid) {
							isActive = true;
							activeIcon = <CustomIcon icon='check-circle' color='#0CBAFF' />;
							containerStyle = style.systemEnabled;
							status = 'enabled';
						}

						for (const [ key, val, ] of Object.entries(assignmentMap)) {
							if (key.startsWith(`system.${systemUUID}`)) {
								pendingChangeStyle = style.systemPendingChange;

								if (key === `system.${systemUUID}.template.${activeTemplate.uuid}`) {
									if (val) {
										templateNameDisplay = activeTemplate.display_name;
										isActive = true;
										activeIcon = <CustomIcon icon='check-circle' color='#0CBAFF' />;
										containerStyle = style.systemEnabled;
										status = 'enabled';
										break;
									} else {
										templateNameDisplay = '';
										isActive = false;
										activeIcon = <CustomIcon icon='circle' color='#DBDBDB' />;
										containerStyle = style.systemEmpty;
										status = 'empty';
									}
								}
							}
						}

						return (
							<div
								className={`${style.systemContainer} ${containerStyle} ${pendingChangeStyle}`}
								key={systemUUID}
								onClick={(e) => {
									if (isActive && isGrouped) {
										this.props.app.openModal({
											modalSize:  1,
											showModal:  true,
											modalProps: {
												omitClose: true,
												title:     'Warning',
												jsx:       (
													<AssignmentWarning
													/>
												),
											},
										});
									} else {
										e.preventDefault();
										this.handleSystemAssignment(systemUUID, status, systemName, templateUUID);
									}
								}}
							>
								<div className={style.systemIcon} data-test-id={`systemAssignmentIcon-${status}`}>{activeIcon}</div>
								<div className={style.systemIcon}>{orientationIcon}</div>
								<div>{systemName}</div>
								<div className={style.resolutionContainer}>
									{resolution} {highResIcon}
								</div>
								<div>{templateNameDisplay}</div>
							</div>
						);
					})}
				</div>
			);
		});
	}

	renderData() {
		const {
			step,
		} = this.props;

		switch (step) {
		case 1:
			return this.renderStepTwo();
		case 2:
			return this.renderStepThree();
		default:
			return '';
		}
	}

	openImageModal(modalTitle, m1, photosValue, settingList) {
		let {
			app,
		} = this.props;
		let photoUrl = '';
		settingList.forEach((model) => {
			if (photosValue === model.setting.name) {
				photoUrl = s3PublicURL(this.state.bucketName, `${vultureENV}${model.text}`);
			}
		});
		let title = m1.split('_')[1];
		title = title[0].toUpperCase() + title.substring(1);

		app.openModal({
			modalSize:  3,
			showModal:  true,
			modalProps: {
				title: `${modalTitle}`,
				jsx:   (
					<div className={style.modalContainer}>
						<img className={style.modalImage} src={photoUrl} alt="" />
					</div>
				),
			},
		});
	}

	openNewDesignModal(isNewDesign = false) {
		const {
			app,
		} = this.props;

		const {
			activeTemplate,
		} = this.state;

		app.openModal({
			modalSize:  2,
			showModal:  true,
			modalProps: {
				title: (isNewDesign || !activeTemplate.uuid) ? 'NEW DESIGN' : 'EDIT DESIGN',
				jsx:   (
					<NewDesignModal
						templateBuilder={this}
						app={app}
						activeTemplate={activeTemplate}
						globalTemplateData={this.props.globalTemplateData}
					/>
				),
			},
		});
	}

	openCopyTemplateModal(sourceTemplateUUID, sourceTemplateData) {
		const {
			app,
		} = this.props;

		app.openModal({
			modalSize:  2,
			showModal:  true,
			modalProps: {
				title: 'COPY DESIGN',
				jsx:   (
					<CopyTemplateModal
						templateBuilder={this}
						app={app}
						existingFlatData={this.state.existingFlatData}
						sourceTemplateUUID={sourceTemplateUUID}
						sourceTemplateData={sourceTemplateData}
						globalTemplateData={this.props.globalTemplateData}
					/>
				),
			},
		});
	}

	renderThemeDetails() {
		if (Object.keys(this.state.theme).length > 0) {
			let notes = [];
			let template_theme = this.state.theme;
			let resolutionsArray = [
				{
					label:        'Horizontal',
					notesValue:   'horizontal_notes',
					photosValue:  'horizontal_image',
					settingValue: 'horizontal',
				},
				{
					label:        'Vertical',
					notesValue:   'portrait_notes',
					photosValue:  'portrait_image',
					settingValue: 'vertical',
				},
				{
					label:        '4K Horizontal',
					notesValue:   '4k_horizontal_notes',
					photosValue:  '4k_horizontal_image',
					settingValue: 'horizontal_4k',
				},
				{
					label:        '4K Vertical',
					notesValue:   '4k_portrait_notes',
					photosValue:  '4k_portrait_image',
					settingValue: 'vertical_4k',
				},
			];
			if (template_theme.setting_list) {
				template_theme.setting_list.forEach((settingMapModel) => {
					let settingOptionSelected = [];
					resolutionsArray.forEach((option, i) => {
						if (settingMapModel.setting.name === option.settingValue) {
							settingOptionSelected = resolutionsArray[i];
						}
					});

					let val = '';
					let photo = '';
					if (settingOptionSelected.length !== 0) {
						template_theme.setting_list.forEach((model) => {
							if (settingOptionSelected.notesValue === model.setting.name) {
								val = model.text;
							}
							if (settingOptionSelected.photosValue === model.setting.name) {
								photo = model.text;
							}
						});

						let photoUrl = s3PublicURL(this.state.bucketName, `${vultureENV}${photo}`);
						let note = (
							<div className={style.noteContainer}>
								<div>
									<p className={style.noteResolutionHeader}>{settingOptionSelected.label}</p>
									<p className={style.noteResolution}>{val ? val : 'There are no notes for this resolution'}</p>
								</div>
								<div className={style.imageContainer}>
									{photo ? <img src={photoUrl}
										className={style.noteImage}
										onClick={(e) => {
											e.preventDefault();
											//this is needed because otherwise it will always open the latest URL in the array. I don't know why.
											this.openImageModal(settingOptionSelected.label, 'template_category', settingOptionSelected.photosValue, template_theme.setting_list);
										}}
									></img> :
										<div className={style.iconContainer}><CustomIcon icon='image' /></div>}
								</div>
							</div>
						);

						notes.push(note);
					}

				});
			}

			return (
				<div>
					<p className={style.notesTitle}>THEME</p>
					<div className={style.notesContainer}>
						{notes}
					</div>
				</div>
			);
		}
	}

	getCategoryOptions() {
		const {
			globalTemplateData,
		} = this.props;

		// Cache category options to avoid recomputing on every render
		if (!this._cachedCategoryOptions || this._lastGlobalTemplateData !== globalTemplateData) {
			this._cachedCategoryOptions = globalTemplateData
				.filter((category) => !HIDDEN_CATEGORY_NAMES.has((category.display_name || '').trim().toLowerCase()))
				.map((category) => {
					return {
						label: category.display_name,
						value: category.uuid,
					};
				}).sort((a, b) => {
					return a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1;
				});
			this._lastGlobalTemplateData = globalTemplateData;
		}
		return this._cachedCategoryOptions;
	}

	render() {
		const {
			activeTemplate,
		} = this.state;
		let buttonClass = 'button button-disabled';
		if (Object.keys(activeTemplate).length > 0) {
			buttonClass = 'button';
		}

		const {
			step,
		} = this.props;
		let dataContainerClass = style.dataContainerLarge;

		const categoryOptions = this.getCategoryOptions();

		return (
			<React.Fragment>
				<div className={style.mainContainer}>
					<div className={style.templateContainer}>
						<div className={style.header}>
							<div className={style.headerLabel}>DESIGNS</div>
							<div className={style.headerBtnContainer}>
								<button
									className='button'
									onClick={(e) => {
										e.preventDefault();

										// Clear state then open modal in callback
										this.invalidateTempStoreWrites();
										this.setState({
											activeTemplate:     {},
											category:           {},
											config:             {},
											theme:              {},
											templateName:       '',
											latestSkeletonData: null,
											storeKey:           null,
										}, () => {
											// Open modal after state is cleared
											this.openNewDesignModal(true);
										});
									}}
									title="New Design"
									data-test-id="newDesignButton"
								>
									<CustomIcon icon='plus' />
								</button>
								<button
									className={`button ${style.deleteButtonSpacing}`}
									onClick={(e) => {
										e.preventDefault();
										this.openDeleteConfirmModal();
									}}
									title="Delete Design"
									data-test-id="deleteDesignButton"
								>
									<CustomIcon icon='trash' />
								</button>
							</div>
						</div>

					<div className={style.searchContainer}>
						<div className={style.searchInputWrapper}>
							<div className={style.searchIcon}>
								<CustomIcon icon='search' />
							</div>
							<input
								type="text"
								className={style.searchInput}
								data-test-id="design-search-input"
								placeholder="Search designs by name, category, config, or theme..."
								value={this.state.searchTerm}
								onChange={(e) => {
									this.setState({
										searchTerm: e.target.value,
									});
								}}
							/>
						</div>
						<div className={style.listingsLimitWrapper}>
							<label htmlFor="listingsLimit" className={style.listingsLimitLabel}>
								Listings Limit:
							</label>
							<input
								id="listingsLimit"
								type="number"
								min="1"
								className={style.listingsLimitInput}
								value={this.state.previewListingsLimit}
								onChange={(e) => {
									const value = e.target.value;
									// Allow empty string while typing, otherwise parse and validate
									if (value === '') {
										this.setState({
											previewListingsLimit: '',
										});
									} else {
										const numValue = parseInt(value, 10);
										this.setState({
											previewListingsLimit: numValue > 0 ? numValue : 1,
										});
									}
								}}
								onBlur={(e) => {
									// On blur, ensure we have a valid number (default to 60 if empty)
									const value = e.target.value;
									if (value === '' || parseInt(value, 10) <= 0) {
										this.setState({
											previewListingsLimit: 60,
										});
									}
								}}
							/>
						</div>
					</div>						<div className={style.templatesScroller}>
							{this.renderTemplates()}
						</div>
					</div>

					<div className={dataContainerClass}>
						{this.renderData()}
					</div>
				</div>
			</React.Fragment>
		);
	}
}

class NewDesignModal extends React.Component {
	constructor(props) {
		super(props);

		const {
			templateBuilder,
		} = props;

		this.state = {
			activeTemplate: templateBuilder.state.activeTemplate,
			category:       templateBuilder.state.category,
			config:         templateBuilder.state.config,
			theme:          templateBuilder.state.theme,
			templateName:   templateBuilder.state.templateName,
			saving:         false,
		};

		this.categoryRef = React.createRef();
		this.configRef = React.createRef();
		this.themeRef = React.createRef();

		// Cache for expensive computations
		this._cachedCategoryList = null;
		this._cachedConfigList = null;
		this._cachedThemeList = null;
		this._lastGlobalTemplateData = null;
		this._lastCategoryUUID = null;
		this._lastConfigUUID = null;
	}

	formatConfigOption(option) {
		const isDisabled = option && option.isDisabled;
		const style = {
			color: isDisabled ? '#999' : undefined,
			cursor: isDisabled ? 'not-allowed' : undefined,
			display: 'flex',
			alignItems: 'center',
		};
		return (
			<div style={style} title={isDisabled ? 'This configuration is not available for the selected category' : undefined}>
				{option && option.label}
			</div>
		);
	}

	getCategoryList() {
		const {
			globalTemplateData,
		} = this.props;

		if (!this._cachedCategoryList || this._lastGlobalTemplateData !== globalTemplateData) {
			this._cachedCategoryList = globalTemplateData
				.filter((template_category) => !HIDDEN_CATEGORY_NAMES.has((template_category.display_name || '').trim().toLowerCase()))
				.map((template_category) => {
					return {
						label: template_category.display_name,
						value: template_category,
					};
				}).sort((a, b) => {
					const labelA = a.label.toLowerCase();
					const labelB = b.label.toLowerCase();
					if (labelA > labelB) return 1;
					if (labelA < labelB) return -1;
					return 0;
				});
			this._lastGlobalTemplateData = globalTemplateData;
		}

		return this._cachedCategoryList;
	}

	getConfigList() {
		const {
			category,
		} = this.state;

		const categoryKey = (category?.display_name || '').trim().toLowerCase();
		const disabledSet = HIDDEN_CONFIG_BY_CATEGORY[categoryKey] || new Set();

		if (!this._cachedConfigList || this._lastCategoryUUID !== category.uuid) {
			this._cachedConfigList = [];
			if (category.template_config_list) {
				category.template_config_list.forEach((mapModel) => {
					if (mapModel.template_config) {
						const cfg = mapModel.template_config;
						const displayNorm = (cfg.display_name || '').trim().toLowerCase();

						if (HIDDEN_CONFIG_NAMES.has(displayNorm)) return;

						const option = {
							label: cfg.display_name,
							value: cfg,
							isDisabled: disabledSet.has(displayNorm),
						};
						this._cachedConfigList.push(option);
					}
				});
			}
			this._cachedConfigList.sort((a, b) => {
				const labelA = a.label.toLowerCase();
				const labelB = b.label.toLowerCase();
				if (labelA > labelB) return 1;
				if (labelA < labelB) return -1;
				return 0;
			});
			this._lastCategoryUUID = category.uuid;
		}

		return this._cachedConfigList;
	}

	getThemeList() {
		const {
			config,
		} = this.state;

		if (!this._cachedThemeList || this._lastConfigUUID !== config.uuid) {
			this._cachedThemeList = [];
			if (config.template_theme_list) {
				config.template_theme_list.forEach((mapModel) => {
					if (mapModel.template_theme) {
						this._cachedThemeList.push({
							label: mapModel.template_theme.display_name,
							value: mapModel.template_theme,
						});
					}
				});
			}
			this._cachedThemeList.sort((a, b) => {
				const labelA = a.label.toLowerCase();
				const labelB = b.label.toLowerCase();
				if (labelA > labelB) return 1;
				if (labelA < labelB) return -1;
				return 0;
			});
			this._lastConfigUUID = config.uuid;
		}

		return this._cachedThemeList;
	}

	async updatePendingTemplateChanges() {
		const {
			templateBuilder,
			app,
		} = this.props;

		const {
			activeTemplate, category, config, theme, templateName,
		} = this.state;

		this.setState({ saving: true, });
		const saved = await templateBuilder.saveTemplate({
			activeTemplate,
			category,
			config,
			theme,
			templateName,
		});

		if (this._unmounted) {
			return;
		}
		if (saved) {
			app.closeModal();
			return;
		}
		this.setState({ saving: false, });
	}

	componentWillUnmount() {
		this._unmounted = true;
	}

	clearTemplateData() {
		const {
			app,
		} = this.props;

		// Close the modal immediately
		// No need to clear state - the component will be unmounted
		// and get fresh state when opened again
		app.closeModal();
	}

	handleDelete() {
		const {
			templateBuilder,
			app,
		} = this.props;

		const {
			activeTemplate,
		} = this.state;

		// Set the active template in the parent before deleting
		templateBuilder.setState({
			activeTemplate,
		}, () => {
			app.closeModal();
			templateBuilder.deleteTemplate();
		});
	}

	openImageModal(modalTitle, m1, photosValue, settingList) {
		const {
			app,
		} = this.props;

		let photoUrl = '';
		settingList.forEach((model) => {
			if (photosValue === model.setting.name) {
				photoUrl = s3PublicURL('ts-theme-editor', `${vultureENV}${model.text}`);
			}
		});

		app.openModal({
			modalSize:  3,
			showModal:  true,
			modalProps: {
				title: `${modalTitle}`,
				jsx:   (
					<div className={style.modalContainer}>
						<img className={style.modalImage} src={photoUrl} alt="" />
					</div>
				),
			},
		});
	}

	renderThemeDetails() {
		if (Object.keys(this.state.theme).length > 0) {
			let notes = [];
			let template_theme = this.state.theme;
			let resolutionsArray = [
				{
					label:        'Horizontal',
					notesValue:   'horizontal_notes',
					photosValue:  'horizontal_image',
					settingValue: 'horizontal',
				},
				{
					label:        'Vertical',
					notesValue:   'portrait_notes',
					photosValue:  'portrait_image',
					settingValue: 'vertical',
				},
				{
					label:        '4K Horizontal',
					notesValue:   '4k_horizontal_notes',
					photosValue:  '4k_horizontal_image',
					settingValue: 'horizontal_4k',
				},
				{
					label:        '4K Vertical',
					notesValue:   '4k_portrait_notes',
					photosValue:  '4k_portrait_image',
					settingValue: 'vertical_4k',
				},
			];
			if (template_theme.setting_list) {
				template_theme.setting_list.forEach((settingMapModel) => {
					let settingOptionSelected = [];
					resolutionsArray.forEach((option, i) => {
						if (settingMapModel.setting.name === option.settingValue) {
							settingOptionSelected = resolutionsArray[i];
						}
					});

					let val = '';
					let photo = '';
					if (settingOptionSelected.length !== 0) {
						template_theme.setting_list.forEach((model) => {
							if (settingOptionSelected.notesValue === model.setting.name) {
								val = model.text;
							}
							if (settingOptionSelected.photosValue === model.setting.name) {
								photo = model.text;
							}
						});

						let photoUrl = s3PublicURL('ts-theme-editor', `${vultureENV}${photo}`);
						let note = (
							<div className={style.noteContainer}>
								<div>
									<p className={style.noteResolutionHeader}>{settingOptionSelected.label}</p>
									<p className={style.noteResolution}>{val ? val : 'There are no notes for this resolution'}</p>
								</div>
								<div className={style.imageContainer}>
									{photo ? <img src={photoUrl}
										className={style.noteImage}
										onClick={(e) => {
											e.preventDefault();
											this.openImageModal(settingOptionSelected.label, 'template_category', settingOptionSelected.photosValue, template_theme.setting_list);
										}}
									></img> :
										<div className={style.iconContainer}><CustomIcon icon='image' /></div>}
								</div>
							</div>
						);

						notes.push(note);
					}

				});
			}

			return (
				<div>
					<p className={style.notesTitle}>THEME</p>
					<div className={style.notesContainer}>
						{notes}
					</div>
				</div>
			);
		}
	}

	renderResolutionPreviews() {
		if (Object.keys(this.state.theme).length === 0) {
			return null;
		}

		const template_theme = this.state.theme;
		const resolutionsArray = [
			{
				label:        'Horizontal',
				photosValue:  'horizontal_image',
				settingValue: 'horizontal',
			},
			{
				label:        'Vertical',
				photosValue:  'portrait_image',
				settingValue: 'vertical',
			},
			{
				label:        '4K Horizontal',
				photosValue:  '4k_horizontal_image',
				settingValue: 'horizontal_4k',
			},
			{
				label:        '4K Vertical',
				photosValue:  '4k_portrait_image',
				settingValue: 'vertical_4k',
			},
		];

		const previews = [];
		if (template_theme.setting_list) {
			resolutionsArray.forEach((resolution) => {
				template_theme.setting_list.forEach((settingMapModel) => {
					if (settingMapModel.setting.name === resolution.settingValue) {
						let photoUrl = '';
						template_theme.setting_list.forEach((model) => {
							if (resolution.photosValue === model.setting.name) {
								photoUrl = s3PublicURL('ts-theme-editor', `${vultureENV}${model.text}`);
							}
						});

						if (resolution.photosValue !== '4k_horizontal_image' && resolution.photosValue !== '4k_portrait_image') {
							previews.push({
								label:       resolution.label,
								photoUrl,
								photosValue: resolution.photosValue,
							});
						}
					}
				});
			});
		}

		return (
			<div className={style.resolutionPreviewsContainer}>
				<div className={style.previewsHeader}>RESOLUTION PREVIEWS</div>
				<div className={style.previewsGrid}>
					{previews.map((preview, i) => {
						return (
							<div key={i} className={style.previewCard}>
								<div className={style.previewLabel}>{preview.label}</div>
								<div className={style.previewImageContainer}>
									{preview.photoUrl ? (
										<img
											src={preview.photoUrl}
											className={style.previewImage}
											alt={preview.label}
											onClick={(e) => {
												e.preventDefault();
												this.openImageModal(preview.label, preview.photosValue, preview.photosValue, this.state.theme.setting_list);
											}}
										/>
									) : (
										<div className={style.previewPlaceholder}>
											<CustomIcon icon='image' />
										</div>
									)}
								</div>
							</div>
						);
					})}
				</div>
			</div>
		);
	}

	render() {
		const {
			activeTemplate, category, config, theme, saving,
		} = this.state;

		// Get values for react-select in case we're editing.
		let categoryValue;
		if (category.uuid) {
			categoryValue = {
				label: category.display_name,
				value: category,
			};
		}

		let configValue;
		if (config.uuid) {
			configValue = {
				label: config.display_name,
				value: config,
			};
		}

		let themeValue;
		if (theme.uuid) {
			themeValue = {
				label: theme.display_name,
				value: theme,
			};
		}

		const configSelectStyles = {
			option: (provided, state) => ({
				...provided,
				color: state.isDisabled ? '#999' : provided.color,
				cursor: state.isDisabled ? 'not-allowed' : provided.cursor,
			}),
			singleValue: (provided, state) => ({
				...provided,
				color: state.data && state.data.isDisabled ? '#999' : provided.color,
			}),
		};

		// Use cached lists for performance
		const categoryList = this.getCategoryList();
		const configList = this.getConfigList();
		const themeList = this.getThemeList();

		return (
			<React.Fragment>
				<div className={style.modalLayout}>
					<div className={style.modalLeftColumn}>
						<div className={style.formGroup}>
							<div className={style.formLabel}>DESIGN NAME</div>
							<input
								className={style.formInput}
								data-test-id="templateFormInput"
								onChange={(e) => {
									this.setState({
										templateName: e.target.value,
									});
								}}
								value={this.state.templateName}
							/>
						</div>

						<div className={style.formGroup}>
							<div className={style.formLabel}>CATEGORY</div>
							<Select
								isClearable
								ref={this.categoryRef}
								options={categoryList}
								components={selectComponents}
								dropdownIndicatorTestId="indicator-category"
								optionTestIdPrefix="option"
								onChange={(e) => {
									this.setState({
										category: e?.value || {},
										config:   {},
										theme:    {},
									});
									this.configRef.current.select.clearValue();
									this.themeRef.current.select.clearValue();
								}}
								{...categoryValue ? {
									value: categoryValue,
								} : null}
							/>
						</div>

						<div className={style.formGroup}>
							<div className={style.formLabel}>CONFIGURATION</div>
							<Select
								isClearable
								{...category.uuid ? null : {
									isDisabled: true,
									value:      null,
								}}
								ref={this.configRef}
								options={configList}
								isOptionDisabled={(opt) => Boolean(opt && opt.isDisabled)}
								styles={configSelectStyles}
								formatOptionLabel={this.formatConfigOption.bind(this)}
								components={selectComponents}
								dropdownIndicatorTestId="indicator-configuration"
								optionTestIdPrefix="option"
								onChange={(e) => {
									this.setState({
										config: e?.value || {},
										theme:  {},
									});
									this.themeRef.current.select.clearValue();
								}}
								{...configValue ? {
									value: configValue,
								} : null}
							/>
						</div>

						<div className={style.formGroup}>
							<div className={style.formLabel}>THEME</div>
							<Select
								isClearable
								{...config.uuid ? null : {
									isDisabled: true,
									value:      null,
								}}
								ref={this.themeRef}
								options={themeList}
								components={selectComponents}
								dropdownIndicatorTestId="indicator-theme"
								optionTestIdPrefix="option"
								onChange={(e) => {
									this.setState({
										theme: e?.value || {},
									});
								}}
								{...themeValue ? {
									value: themeValue,
								} : null}
							/>
						</div>

						<div className={style.modalActions}>
							{activeTemplate.uuid && (
								<button
									className={`button-outlined ${style.deleteButton}`}
									style={{
										marginRight: 'auto',
									}}
									disabled={saving}
									onClick={(e) => {
										e.preventDefault();
										this.handleDelete();
									}}
								>
									Delete
								</button>
							)}
							<button
								className='button-outlined'
								disabled={saving}
								onClick={(e) => {
									e.preventDefault();
									this.clearTemplateData();
								}}
							>
								Cancel
							</button>
							<button
								className='button'
								data-test-id="save-changes"
								disabled={saving}
								onClick={(e) => {
									e.preventDefault();
									this.updatePendingTemplateChanges();
								}}
							>
								Save Changes
							</button>
						</div>
					</div>

					<div className={style.modalRightColumn}>
						{this.renderResolutionPreviews()}
					</div>
				</div>
			</React.Fragment>
		);
	}
}

class CopyTemplateModal extends React.Component {
	constructor(props) {
		super(props);

		const {
			sourceTemplateData,
			templateBuilder,
			existingFlatData,
		} = props;


		// Default the template name to "Copy of [Original Name]"
		const defaultName = `Copy of ${sourceTemplateData.display_name}`;

		// Get current account info for default selection
		const currentAccountUUID = templateBuilder.props.accountUUID;

		this.state = {
			templateName:     defaultName,
			category:         sourceTemplateData.category,
			config:           sourceTemplateData.config,
			theme:            sourceTemplateData.theme,
			targetAccount:    null, // Will be set after fetching default account
			loadingAccounts:  true,
			currentAccountUUID,
		};
	}

	componentDidMount() {
 // Load default account on mount
		this.loadDefaultAccount();
	}

	async loadDefaultAccount() {
		const { currentAccountUUID } = this.state;
		const { app } = this.props;

		try {
			// Get current account from app state
			const accountList = app.state.accountList || [];
			const currentAccountObj = accountList.find((accountObj) => {
				const uuid = Object.keys(accountObj)[0];
				return uuid === currentAccountUUID;
			});

			if (currentAccountObj) {
				const uuid = Object.keys(currentAccountObj)[0];
				const currentAccountOption = {
					label: currentAccountObj[uuid],
					value: uuid,
				};

				this.setState({
					targetAccount:   currentAccountOption,
					loadingAccounts: false,
				});
			} else {
				this.setState({
					loadingAccounts: false,
				});
			}
		} catch (err) {
			console.error('Error loading default account:', err);
			this.setState({
				loadingAccounts: false,
			});
		}
	}

	// Search accounts using the /account/search endpoint
	searchAccounts = async (inputValue) => {
		const sessionKey = getSession().session;
		const url = `${broncoURL}/account/search`;

		// Build request body
		// Note: Backend returns 404 if both col and search are provided but search is empty
		// So for initial load with no input, we send a minimal search that matches most accounts
		const body = (inputValue && inputValue.length >= 1)
			? {
				col:    'name',
				search: inputValue,
			}
			: {
				// Send a single space or % to match all/most accounts
				// This allows internal admins to see all accounts
				col:    'name',
				search: '%',
			};

		const opts = {
			method:  'POST',
			headers: {
				'content_type':   'application/json',
				'X-Auth-Session': sessionKey,
			},
			body,
		};

		try {
			const response = await new Promise((resolve, reject) => {
				new kali(opts).get(url, {
					success: (_kali, res, data) => {
						resolve(data);
					},
					failure: (_kali, err) => {
						reject(err);
					},
				}, true);
			});

			const accountOptions = [];

			if (response) {
				Object.entries(response).forEach(([ uuid, accountData ]) => {
					accountOptions.push({
						label: accountData.display_name || accountData.name || 'Unknown Account',
						value: uuid,
					});
				});
			}

			// Sort alphabetically
			accountOptions.sort((a, b) => {
				return a.label.localeCompare(b.label);
			});

			return accountOptions;
		} catch (err) {
			console.error('Error searching accounts:', err);
			// Fallback to app.state.accountList if API fails
			const { app } = this.props;
			const accountList = app.state.accountList || [];
			return accountList.map((accountObj) => {
				const uuid = Object.keys(accountObj)[0];
				return {
					label: accountObj[uuid],
					value: uuid,
				};
			});
		}
	}

	async copyTemplate() {
		const {
			templateBuilder,
			app,
			sourceTemplateUUID,
		} = this.props;

		const {
			templateName,
			targetAccount,
			includeListingConfigs,
		} = this.state;

		if (!templateName || !templateName.trim()) {
			alert('Please enter a name for the copied template.');
			return;
		}

		if (!targetAccount) {
			alert('Please select a target account.');
			return;
		}

		const targetAccountUUID = targetAccount.value;
		const currentAccountUUID = templateBuilder.props.accountUUID;

		try {
			// Show loading indicator
			app.setState({
				loading: true,
			});

			// Clear the active template BEFORE saving to prevent React from trying to render incomplete data
			templateBuilder.clearTemplateData();

			// Call the backend API to clone the template
			const result = await API.cloneTemplate(
				sourceTemplateUUID,
				targetAccountUUID,
				templateName.trim(),
				includeListingConfigs
			);

			if (!result.success) {
				throw new Error(result.error || 'Failed to clone template');
			}

			// Close modal after successful save
			app.closeModal();

			// Refresh templates - fetch the target account's templates
			if (targetAccountUUID === currentAccountUUID) {
				// Same account - refresh globalTemplateData to ensure all data is in sync
				// This prevents issues where editing the copied template shows changes on the original
				if (templateBuilder.props.refreshGlobalTemplateData) {
					templateBuilder.props.refreshGlobalTemplateData();
				}
				// Refresh current view
				await templateBuilder.props.app.fetchAccountTemplates(targetAccountUUID);
				app.setState({
					loading: false,
				});
			} else {
				// Different account - switch to the new account and load it
				// Keep loading state active during account switch
				app.setAccountUUID(targetAccountUUID);
				// Loading state will be cleared by the account switch process
			}
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('Error cloning template:', err);
			// eslint-disable-next-line no-alert
			alert('Failed to copy template: ' + (err.message || 'Unknown error'));
			// Clear loading state on error
			app.setState({
				loading: false,
			});
		}
	}

	renderSourceTemplateInfo() {
		const {
			sourceTemplateData,
		} = this.props;

		let customIcon = null;
		if (sourceTemplateData?.config?.is_global === false) {
			customIcon = <CustomIcon icon='code' color='#C93A6A' />;
		}

		return (
			<div className={style.sourceTemplateInfo}>
				<div className={style.sourceTemplateInfoTitle}>Copying from:</div>
				<div className={style.sourceTemplateInfoContent}>
					<div className={style.sourceTemplateName}>{sourceTemplateData.display_name}</div>
					<div className={style.templateData}>
						<div className={style.templateDataKey}>Category:</div>
						<div className={style.templateDataValue}>{sourceTemplateData.category.display_name}</div>
					</div>
					<div className={style.templateData}>
						<div className={style.templateDataKey}>Configuration:</div>
						<div className={style.templateDataValue}>{customIcon} {sourceTemplateData?.config?.display_name || 'BAD_CONFIG'}</div>
					</div>
					<div className={style.templateData}>
						<div className={style.templateDataKey}>Theme:</div>
						<div className={style.templateDataValue}>{sourceTemplateData.theme.display_name}</div>
					</div>
				</div>
			</div>
		);
	}

	render() {
		return (
			<React.Fragment>
				<div className={style.modalLayout}>
					<div className={style.modalLeftColumn}>
						{this.renderSourceTemplateInfo()}

						<div className={style.formGroup}>
							<div className={style.formLabel}>
								<div>NEW DESIGN NAME</div>
								<div
									style={{
										fontSize: '0.85em',
									}}
								>
									(Please avoid Duplicate Template Names)
								</div>
							</div>
							<input
								className={style.formInput}
								data-test-id="templateFormInput"
								onChange={(e) => {
									this.setState({
										templateName: e.target.value,
									});
								}}
								value={this.state.templateName}
								placeholder="Enter a name for the copied template"
							/>
						</div>

						<div className={style.formGroup}>
							<div className={style.formLabel}>TARGET ACCOUNT</div>
							<AsyncSelect
								defaultOptions
								loadOptions={this.searchAccounts}
								value={this.state.targetAccount}
								components={selectComponents}
								dropdownIndicatorTestId="indicator-target-account"
								optionTestIdPrefix="option"
								onChange={(selectedOption) => {
									const isSameAccount = selectedOption && selectedOption.value === this.state.currentAccountUUID;
									this.setState({
										targetAccount: selectedOption,
										...(isSameAccount && { includeListingConfigs: false }),
									});
								}}
								isLoading={this.state.loadingAccounts}
								isDisabled={this.state.loadingAccounts}
								placeholder={this.state.loadingAccounts ? 'Loading accounts...' : 'Search for account by name...'}
								className={style.selectInput}
								noOptionsMessage={() => {
									return 'No accounts found';
								}}
							/>
						</div>

						{this.state.targetAccount && this.state.targetAccount.value !== this.state.currentAccountUUID && (
							<div className={style.formGroup}>
								<label className={style.checkboxLabel}>
									<input
										type="checkbox"
										checked={this.state.includeListingConfigs}
										data-test-id="include-listing-configurations"
										onChange={(e) => {
											this.setState({
												includeListingConfigs: e.target.checked,
											});
										}}
									/>
									<span className={style.checkboxText}>
										Include Listing Configurations (directory data)
									</span>
								</label>
								<div className={style.checkboxHelp}>
									If unchecked, listing configuration references will be removed from the copied template.
								</div>
							</div>
						)}

						<div className={style.modalActions}>
							<button
								className='button-outlined'
								data-test-id="close-modal"
								onClick={(e) => {
									e.preventDefault();
									this.props.app.closeModal();
								}}
							>
								Cancel
							</button>
							<button
								className='button'
								data-test-id="copy-design"
								onClick={(e) => {
									e.preventDefault();
									this.copyTemplate();
								}}
							>
								Copy Design
							</button>
						</div>
					</div>

					<div className={style.modalRightColumn}>
						<div className={style.copyModalNote}>
							<CustomIcon icon='info-circle' color='#3b82f6' />
							<div>
								<strong>Note:</strong> The copied design will use the same category, configuration, and theme as the source design.
							</div>
						</div>
					</div>
				</div>
			</React.Fragment>
		);
	}
}


class AssignmentWarning extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			value: props.value,
		};
	}
	render() {
		return (
			<React.Fragment>
				<div className={`${style.listingWarningModalContainer} ${style.overflowHidden}`}>
					<p>You must remove this system from its Group, before unassigning the Template.</p>
					<div className={style.warningModalButton}>
						<button className="button"	onClick={(e) => {
							e.preventDefault();
							this.props.app.closeModal();
						}}><CustomIcon icon='undo' />Go Back</button>
					</div>
				</div>
			</React.Fragment>
		);
	}
}

export {
	NewDesignModal,
};

export default TemplateBuilder;